import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { AdRulesContent } from "@/components/legal/AdRulesContent";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/regler")({
  head: () => ({
    meta: [
      { title: "Annonsregler på Sellavo – vad som gäller för text och bilder" },
      {
        name: "description",
        content:
          "Reglerna för att annonsera på Sellavo: inga telefonnummer, e-post eller länkar i annonsen, regler för bilder med människor, samtycke och ansvar.",
      },
      { property: "og:title", content: "Annonsregler på Sellavo" },
      {
        property: "og:description",
        content:
          "Så ska en annons se ut på Sellavo: tillåtet innehåll, förbjudet innehåll och reglerna för bilder med personer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/regler" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/regler" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Annonsregler på Sellavo",
          url: "/regler",
          description:
            "Reglerna för att annonsera på Sellavo: inga telefonnummer, e-post eller länkar i annonsen, samt regler för bilder med människor.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "/" },
            { "@type": "ListItem", position: 2, name: "Annonsregler", item: "/regler" },
          ],
        }),
      },
    ],
  }),
  component: ReglerPage,
});

export function ReglerPage() {
  const { lang } = useLocale();
  const tx = lang === "en" ? { title: "Listing rules", intro: "Short, clear rules that keep Sellavo safe for buyers and sellers. We automatically review listing text and images before publication.", questions: "Questions? Read more", about: "about Sellavo", or: "or see", plans: "plans and limits" } : lang === "es" ? { title: "Reglas de anuncios", intro: "Reglas breves y claras para que Sellavo sea seguro para compradores y vendedores. Revisamos automáticamente el texto y las imágenes antes de publicar.", questions: "¿Tienes preguntas? Lee más", about: "sobre Sellavo", or: "o consulta", plans: "planes y límites" } : null;
  useEffect(() => {
    document.getElementById("app-scroll")?.scrollTo({ top: 0, behavior: "instant" });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <main className="bg-background">
      <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            {tx?.title ?? "Annonsregler"}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ocean-100 sm:text-lg">
            {tx?.intro ?? "Korta och tydliga regler som gör Sellavo tryggt för både köpare och säljare. Vi granskar annonstext och bilder automatiskt innan de publiceras."}
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <div className="mx-auto grid max-w-4xl gap-5">
          <AdRulesContent />

          <p className="text-sm text-muted-foreground">
            {tx?.questions ?? "Frågor? Läs mer"}{" "}
            <Link to={localizedPath("/om", lang)} className="text-ocean-500 underline underline-offset-2">
              {tx?.about ?? "om Sellavo"}
            </Link>{" "}
            {tx?.or ?? "eller se"}{" "}
            <Link to={localizedPath("/priser", lang)} className="text-ocean-500 underline underline-offset-2">
              {tx?.plans ?? "planer och gränser"}
            </Link>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
